import React, { useState } from "react";
import {
  Title,
  Button,
  Group,
  Stack,
  Paper,
  Collapse,
  Text,
  Badge,
  Grid,
  LoadingOverlay,
} from "@mantine/core";
import { IconPlus, IconEdit } from "@tabler/icons-react";
import { useServiceAdjustmentForms } from "../../context/ServiceAdjustmentFormsContext";
import { useSalon } from "../../context/SalonContext";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import AdjustmentFormWizard from "./AdjustmentFormWizard";
import styles from "./AdjustmentFormsTab.module.css";

function AdjustmentFormsTab({ canCRUD = true }) {
  const { adjustmentForms, isLoading, deleteAdjustmentForm, formJunctions } =
    useServiceAdjustmentForms();
  const { currentUser, services } = useSalon();
  const [expandedForm, setExpandedForm] = useState(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleFormClick = (form) => {
    setExpandedForm(
      expandedForm?.service_adjustment_form_id ===
        form.service_adjustment_form_id
        ? null
        : form,
    );
  };

  const handleAddClick = () => {
    setEditingForm(null);
    setIsWizardOpen(true);
  };

  const handleEditClick = (e, form) => {
    e.stopPropagation();
    setEditingForm(form);
    setIsWizardOpen(true);
  };

  const handleDeleteClick = (e, form) => {
    e.stopPropagation();
    setFormToDelete(form);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!formToDelete) return;

    try {
      setIsDeleting(true);
      await deleteAdjustmentForm(formToDelete.service_adjustment_form_id);
      setIsDeleteModalOpen(false);
      setFormToDelete(null);
    } catch (error) {
      console.error("Error deleting form:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleWizardSuccess = () => {
    setIsWizardOpen(false);
    setEditingForm(null);
  };

  const getConnectedServicesCount = (formId) => {
    return formJunctions.filter(
      (junction) => junction.service_adjustment_form_id === formId,
    ).length;
  };

  return (
    <>
      <LoadingOverlay visible={isLoading} />
      <Stack p="lg">
        <Group justify="space-between">
          <Title order={3}>Service Adjustment Forms</Title>
          {canCRUD && (
            <Button
              leftSection={<IconPlus size="1rem" />}
              color="teal"
              onClick={handleAddClick}
            >
              Add Service Adjustment Form
            </Button>
          )}
        </Group>

        {adjustmentForms.length === 0 ? (
          <Paper withBorder p="xl" radius="md">
            <Stack align="center" gap="md">
              <Text c="dimmed" size="lg">
                No adjustment forms created yet
              </Text>
              {canCRUD && (
                <Button
                  leftSection={<IconPlus size="1rem" />}
                  color="teal"
                  onClick={handleAddClick}
                >
                  Create Your First Form
                </Button>
              )}
            </Stack>
          </Paper>
        ) : (
          <Stack spacing="md">
            {adjustmentForms.map((form) => (
              <Paper
                key={form.service_adjustment_form_id}
                withBorder
                className={`${styles.formItem} ${
                  expandedForm?.service_adjustment_form_id ===
                  form.service_adjustment_form_id
                    ? styles.selected
                    : ""
                }`}
                onClick={() => handleFormClick(form)}
                radius="md"
              >
                <Group justify="space-between" p="md">
                  <Group gap="md">
                    <Text fw={500}>{form.name}</Text>
                    <Badge
                      className={`${styles.activeBadge} ${
                        form.is_active ? styles.active : styles.inactive
                      }`}
                      variant="light"
                      color={form.is_active ? "green" : "gray"}
                    >
                      {form.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </Group>
                  <Group>
                    {canCRUD && (
                      <>
                        <Button
                          variant="light"
                          color="teal"
                          size="xs"
                          leftSection={<IconEdit size="1rem" />}
                          onClick={(e) => handleEditClick(e, form)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="subtle"
                          color="red"
                          size="xs"
                          onClick={(e) => handleDeleteClick(e, form)}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </Group>
                </Group>

                <Collapse
                  in={
                    expandedForm?.service_adjustment_form_id ===
                    form.service_adjustment_form_id
                  }
                >
                  <Stack spacing="xs" p="md" pt={0}>
                    <Grid>
                      <Grid.Col span={6}>
                        <Stack gap={0}>
                          <Text fw={500} size="sm">
                            Description
                          </Text>
                          {form.description ? (
                            <Text size="sm">{form.description}</Text>
                          ) : (
                            <Text size="sm" c="dimmed">
                              No description provided
                            </Text>
                          )}
                        </Stack>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Stack gap={0}>
                          <Text fw={500} size="sm">
                            Priority
                          </Text>
                          <Text size="sm">{form.priority}</Text>
                        </Stack>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Stack gap={0}>
                          <Text fw={500} size="sm">
                            Questions
                          </Text>
                          <Text size="sm">
                            {form.questions?.length || 0} question
                            {form.questions?.length !== 1 ? "s" : ""}
                          </Text>
                        </Stack>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Stack gap={0}>
                          <Text fw={500} size="sm">
                            Connected Services
                          </Text>
                          <Text size="sm">
                            {getConnectedServicesCount(
                              form.service_adjustment_form_id,
                            )}{" "}
                            service
                            {getConnectedServicesCount(
                              form.service_adjustment_form_id,
                            ) !== 1
                              ? "s"
                              : ""}
                          </Text>
                        </Stack>
                      </Grid.Col>
                    </Grid>
                  </Stack>
                </Collapse>
              </Paper>
            ))}
          </Stack>
        )}
      </Stack>

      <AdjustmentFormWizard
        isOpen={isWizardOpen}
        onClose={() => {
          setIsWizardOpen(false);
          setEditingForm(null);
        }}
        initialData={editingForm}
        onSuccess={handleWizardSuccess}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setFormToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Adjustment Form"
        message={`Are you sure you want to delete "${formToDelete?.name}"? This action cannot be undone.`}
        isLoading={isDeleting}
      />
    </>
  );
}

export default AdjustmentFormsTab;
